// Pure helpers of the generative UI (no React, no i18n): markers in the reply text, allowed
// views per tool, grouping of the items for the by_* bar charts.

// Views per tool, the first one is the default (back/src/apps/ai_assistant, contract section 3)
export const TOOL_VIEWS = {
  search_companies: ["list", "by_status", "by_team"],
  get_company: ["card"],
  list_company_tasks: ["list", "by_status", "by_service"],
  list_company_subtasks: ["list", "by_status", "by_operator"],
  list_my_subtasks: ["list", "by_status", "by_company", "by_due"],
  list_company_notes: ["list"],
  list_company_services: ["list"],
  my_notifications: ["list"],
  list_operators: ["list", "by_team"],
  company_payments_summary: ["summary"],
};

// The asked view when the tool allows it, its default view otherwise
export const resolveView = (tool, view) => {
  const views = TOOL_VIEWS[tool];
  if (!views) return null;
  return view && views.includes(view) ? view : views[0];
};

// A whole line "[[ui:r1]]" or "[[ui:r1|by_status]]"
// (any view text: resolveView falls back to the default view for an unknown one, as the backend)
// These patterns are a light second pass: the backend already cleans every reply (unclosed
// and nested markers included), so a marker it lets through always has this simple shape.
const UI_LINE_RE = /^\s*\[\[ui:(r\d+)(?:\|([^\]\n]*))?\]\]\s*$/;
const UI_ANY_RE = /\[\[ui:[^\]\n]*\]\]/g;
const SUGGEST_RE = /\[\[suggest:[^\]\n]*\]\]/g;

/**
 * Splits a reply into text and block parts:
 * [{ type: "text", text }, { type: "ui", ref, view }]. Leftover suggest markers and markers that
 * are not on a line of their own are dropped.
 */
export const splitReply = (content) => {
  const parts = [];
  let buffer = [];
  const flush = () => {
    const text = buffer.join("\n").trim();
    if (text) parts.push({ type: "text", text });
    buffer = [];
  };
  String(content ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .forEach((line) => {
      const match = line.match(UI_LINE_RE);
      if (match) {
        flush();
        parts.push({ type: "ui", ref: match[1], view: (match[2] || "").trim() || null });
        return;
      }
      buffer.push(line.replace(SUGGEST_RE, "").replace(UI_ANY_RE, ""));
    });
  flush();
  return parts;
};

// Reply text without any marker (copy button)
export const stripMarkers = (content) =>
  String(content ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => !UI_LINE_RE.test(line))
    .map((line) => line.replace(SUGGEST_RE, "").replace(UI_ANY_RE, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

export const NONE_KEY = "__none__";

/**
 * Counts the items per value of `keyOf(item)` (a string, or a list of strings: every value
 * counts once). Empty values go to NONE_KEY. Returns [{ key, count }] sorted by count, then by
 * key, with NONE_KEY last; `order` (list of keys) forces a fixed order instead.
 */
export const groupCount = (items, keyOf, order) => {
  const counts = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const raw = keyOf(item);
    const values = (Array.isArray(raw) ? raw : [raw])
      .map((value) => (value === null || value === undefined ? "" : String(value).trim()))
      .filter(Boolean);
    const keys = values.length ? [...new Set(values)] : [NONE_KEY];
    keys.forEach((key) => counts.set(key, (counts.get(key) || 0) + 1));
  });
  const rows = [...counts.entries()].map(([key, count]) => ({ key, count }));
  if (order) {
    const rank = (key) => {
      const index = order.indexOf(key);
      return index === -1 ? order.length + (key === NONE_KEY ? 1 : 0) : index;
    };
    return rows.sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
  }
  return rows.sort((a, b) => {
    if (a.key === NONE_KEY) return 1;
    if (b.key === NONE_KEY) return -1;
    return b.count - a.count || a.key.localeCompare(b.key);
  });
};

// "YYYY-MM-DD" (or "YYYY-MM-DD HH:MM") as a local date at midnight; null when unreadable
export const parseDay = (value) => {
  const match = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Whole days from today to the day (negative = past); null without a date
export const daysFromToday = (value, now = new Date()) => {
  const day = parseDay(value);
  if (!day) return null;
  return Math.round((day - startOfDay(now)) / 86400000);
};

export const DUE_BUCKETS = ["overdue", "today", "tomorrow", "thisWeek", "later", "none"];

/**
 * Due bucket of a date: overdue, today, tomorrow, thisWeek (until Sunday of the current week),
 * later, none (no date).
 */
export const dueBucket = (value, now = new Date()) => {
  const days = daysFromToday(value, now);
  if (days === null) return "none";
  if (days < 0) return "overdue";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  // getDay(): 0 = Sunday. Days left until Sunday included
  const untilSunday = (7 - now.getDay()) % 7;
  return days <= untilSunday ? "thisWeek" : "later";
};

// Same rule as AiMarkdown: "/x" only ("//host" and "/\host" would leave the app)
export const isInternalHref = (href) => typeof href === "string" && /^\/(?![/\\])/.test(href);
